import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';


const DishesManagement = () => {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    categoria: 'prato principal',
    imagem: ''
  });

  const categories = [
    { value: 'prato principal', label: 'Prato Principal' },
    { value: 'arroz', label: 'Arroz' },
    { value: 'feijao', label: 'Feijão' },
    { value: 'massa', label: 'Massa' },
    { value: 'salada', label: 'Salada' },
    { value: 'guarnição', label: 'Guarnição' },
    { value: 'sobremesa', label: 'Sobremesa' },
    { value: 'suco', label: 'Suco' }
  ];



  // Filtros e ordenação
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const validateForm = () => {
    const errors = {};
    if (!formData.nome || formData.nome.trim().length < 2) {
      errors.nome = 'Nome é obrigatório';
    }
    if (!formData.categoria) {
      errors.categoria = 'Categoria é obrigatória';
    }
    setFormErrors(errors);
    return errors;
  };

  // Helper: obter data de criação (fallback pelo ObjectId se necessário)
  const getDishCreationDate = (item) => {
    if (item?.createdAt) {
      try { return new Date(item.createdAt); } catch (_) { /* ignore */ }
    }
    if (item?._id && typeof item._id === 'string' && item._id.length >= 8) {
      try {
        const ts = parseInt(item._id.substring(0, 8), 16) * 1000;
        return new Date(ts);
      } catch (_) { /* ignore */ }
    }
    return null;
  };

  const formatDateTime = (date) => {
    if (!date || isNaN(date.getTime())) return null;
    const d = date.toLocaleDateString('pt-BR');
    const t = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${d} ${t}`;
  };

  const getDishCreatedLabel = (item) => {
    const dt = getDishCreationDate(item);
    const formatted = formatDateTime(dt);
    return formatted || '—';
  };

  const loadDishes = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminService.getDishes({
        search: appliedSearch || undefined,
        category: selectedCategory || undefined,
        sortBy,
        sortOrder,
        limit: 1000,
        page: 1
      });
      setDishes(items);
    } catch (error) {
      console.error('Error loading dishes:', error);
      showToast('Erro ao carregar pratos', 'error');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, selectedCategory, sortBy, sortOrder]);

  useEffect(() => {
    loadDishes();
  }, [loadDishes]);

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      categoria: 'prato principal',
      imagem: ''
    });
    setEditingDish(null);
    setShowForm(false);
    setHasUnsavedChanges(false);
    setFormErrors({});
    setImageFile(null);
    setImagePreviewUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      showToast('Por favor, corrija os erros no formulário', 'error');
      return;
    }
    setFormLoading(true);
    try {
      let dishId = null;
      if (editingDish) {
        await adminService.updateDish(editingDish._id, formData);
        dishId = editingDish._id;
        showToast('Prato atualizado com sucesso!', 'success');
      } else {
        const created = await adminService.createDish(formData);
        dishId = created?.data?.item?._id || created?.item?._id || created?._id || null;
        showToast('Prato criado com sucesso!', 'success');
      }

      // Upload da imagem (arquivo) se selecionado
      if (imageFile && dishId) {
        try {
          const uploadRes = await adminService.uploadDishImage(dishId, imageFile);
          const newUrl = uploadRes?.data?.item?.imagem || uploadRes?.data?.image?.url;
          if (newUrl) {
            setImagePreviewUrl(newUrl);
          }
          showToast('Imagem enviada com sucesso!', 'success');
        } catch (err) {
          console.error('Erro ao enviar imagem do prato:', err);
          showToast(err.message || 'Erro ao enviar imagem do prato', 'error');
        }
      }
      resetForm();
      loadDishes();
    } catch (error) {
      console.error('Error saving dish:', error);
      // Mapear erros de validação campo a campo, se disponíveis
      if (error.details && typeof error.details === 'object') {
        const newErrors = {};
        Object.keys(error.details).forEach((field) => {
          const errObj = error.details[field];
          const msg = errObj?.message || String(errObj);
          newErrors[field] = msg;
        });
        setFormErrors(newErrors);
      }
      if (error.message && error.message.includes('403')) {
        showToast('Acesso negado. Verifique suas permissões.', 'error');
      } else if (error.message && error.message.includes('401')) {
        showToast('Sessão expirada. Faça login novamente.', 'error');
      } else {
        showToast(error.message || 'Erro ao salvar prato', 'error');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este prato?')) {
      return;
    }
    try {
      await adminService.deleteDish(id);
      showToast('Prato excluído com sucesso!', 'success');
      loadDishes();
    } catch (error) {
      console.error('Error deleting dish:', error);
      showToast(error.message || 'Erro ao excluir prato', 'error');
    }
  };

  const handleEdit = (dish) => {
    setEditingDish(dish);
    setFormData({
      nome: dish.nome || '',
      descricao: dish.descricao || '',
      categoria: dish.categoria || 'prato principal',
      imagem: dish.imagem || ''
    });
    setImageFile(null);
    setImagePreviewUrl(dish.imagem || '');
    setShowForm(true);
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    const icon = type === 'success' ? 'ph-check-circle' : 'ph-x-circle';

    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform`;
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="ph ${icon} text-lg mr-2"></i>
        <span>${message}</span>
      </div>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);

    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  const groupedDishes = dishes.reduce((acc, item) => {
    const cat = item.categoria || 'Outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gerenciamento de Pratos</h2>
            <p className="text-gray-600 mt-1">Cadastre, edite e remova pratos</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg flex items-center transition-colors w-full sm:w-auto justify-center"
          >
            <i className="ph ph-plus mr-2"></i>
            Adicionar Prato
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingDish ? 'Editar Prato' : 'Adicionar Prato'}
                </h3>
                <button
                  onClick={() => {
                    if (hasUnsavedChanges && !window.confirm('Você tem alterações não salvas. Deseja sair?')) return;
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="ph ph-x"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => { setFormData({ ...formData, nome: e.target.value }); setHasUnsavedChanges(true); }}
                        className={`w-full border ${formErrors.nome ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500`}
                        placeholder="Ex: Frango Assado"
                      />
                      {formErrors.nome && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.nome}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                      <select
                        value={formData.categoria}
                        onChange={(e) => { setFormData({ ...formData, categoria: e.target.value }); setHasUnsavedChanges(true); }}
                        className={`w-full border ${formErrors.categoria ? 'border-red-300' : 'border-gray-300'} rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500`}
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                      {formErrors.categoria && (
                        <p className="text-red-600 text-sm mt-1">{formErrors.categoria}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                    <textarea
                      value={formData.descricao}
                      onChange={(e) => { setFormData({ ...formData, descricao: e.target.value }); setHasUnsavedChanges(true); }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      rows="3"
                      placeholder="Descreva o prato brevemente"
                    ></textarea>
                  </div>



                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Imagem do Prato</label>
                    <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 p-4 sm:p-6 transition-colors">
                      <div className="flex items-center justify-center text-center">
                        <div className="space-y-3">
                          <div className="text-3xl">📷</div>
                          <p className="text-sm text-gray-700">Arraste e solte a imagem aqui</p>
                          <p className="text-xs text-gray-500">ou</p>
                          <label className="inline-flex items-center px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                setImageFile(file || null);
                                setImagePreviewUrl(file ? URL.createObjectURL(file) : '');
                                setHasUnsavedChanges(true);
                              }}
                              className="hidden"
                            />
                            Selecionar arquivo
                          </label>
                          <p className="text-xs text-gray-500">Máx 25MB · JPG · PNG · WEBP · GIF</p>
                        </div>
                      </div>

                      {(imagePreviewUrl || formData.imagem) && (
                        <div className="mt-4">
                          <div className="text-xs text-gray-600 mb-2">Pré-visualização</div>
                          <img
                            src={imagePreviewUrl || formData.imagem}
                            alt="Pré-visualização da imagem do prato"
                            className="w-64 h-48 sm:w-80 sm:h-60 object-cover rounded-xl border border-gray-200 shadow-sm"
                          />
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => { setImageFile(null); setImagePreviewUrl(''); }}
                              className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                            >
                              Remover imagem
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (hasUnsavedChanges && !window.confirm('Você tem alterações não salvas. Deseja sair?')) return;
                        resetForm();
                      }}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                    >
                      {formLoading ? 'Salvando...' : (editingDish ? 'Salvar alterações' : 'Adicionar Prato')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Barra de busca e filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start md:items-end">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todas</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>



          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="createdAt">Data de criação</option>
              <option value="nome">Nome</option>
            </select>
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Ordem</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start md:items-end mt-2">
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Busca</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome ou descrição"
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="md:col-span-1 flex gap-2 items-end">
            <button
              onClick={() => { setSearchQuery(''); setAppliedSearch(''); setSelectedCategory(''); setSortBy('createdAt'); setSortOrder('desc'); }}
              className="w-1/2 text-sm text-gray-600 hover:text-gray-800"
            >
              Limpar
            </button>
            <button
              onClick={() => setAppliedSearch(searchQuery)}
              className="w-1/2 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg transition-colors"
            >
              Pesquisar
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {(selectedCategory ? categories.filter(c => c.value === selectedCategory) : categories).map(category => {
          const items = groupedDishes[category.value] || [];
          if (items.length === 0) return null;

          return (
            <div key={category.value} className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {category.label} ({items.length})
              </h3>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto overscroll-contain pr-2">
                {items.map(item => (
                  <div key={item._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      {item.imagem && (
                        <img src={item.imagem} alt={item.nome} className="w-16 h-16 object-cover rounded-lg" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-gray-900 font-medium">{item.nome}</h4>
                        </div>
                        {item.descricao && (
                          <p className="text-gray-600 text-sm">{item.descricao}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">Criado em: {getDishCreatedLabel(item)}</p>

                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <i className="ph ph-pencil"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <i className="ph ph-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {dishes.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ph ph-bowl-food text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum prato cadastrado</h3>
          <p className="text-gray-600 mb-4">Comece adicionando o primeiro prato.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Adicionar Primeiro Prato
          </button>
        </div>
      )}
    </div>
  );
};

export default DishesManagement;